import { UserService } from '../api/generated/requests/services.gen';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResult {
  success: boolean;
  error?: string;
  tokens?: {
    access_token: string;
    refresh_token: string;
    id_token?: string;
  };
}

export const LoginService = {
  /**
   * Logowanie przez Keycloak REST API
   */
  authenticateUser: async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      // Krok 1: Weryfikacja dostępu do domeny
      try {
        await UserService.postApiUserVerifyDomainAccess({
          username: credentials.username
        });
      } catch (domainError: any) {
        // Jeśli backend zwrócił 401, nie wysyłaj request do Keycloak
        if (domainError?.status === 401) {
          return {
            success: false,
            error: 'Nieprawidłowe dane logowania'
          };
        }
        // Dla innych błędów (400, 500) również nie kontynuuj
        return {
          success: false,
          error: 'Błąd weryfikacji dostępu do domeny'
        };
      }

      // Krok 2: Jeśli weryfikacja domeny przeszła pomyślnie, kontynuuj z Keycloak
      const tokenUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`;
      
      const formData = new URLSearchParams();
      formData.append('client_id', process.env.REACT_APP_KEYCLOAK_CLIENT_ID!);
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      formData.append('grant_type', 'password');
      formData.append('scope', 'openid profile email');

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: LoginService.mapKeycloakError(errorData.error_description || errorData.error)
        };
      }

      const tokens = await response.json();
      
      return {
        success: true,
        tokens: {
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token
        }
      };

    } catch (error) {
      console.error('Authentication error:', error);
      return {
        success: false,
        error: 'Błąd połączenia z serwerem autoryzacji'
      };
    }
  },

  /**
   * Mapowanie błędów Keycloak na przyjazne komunikaty
   */
  mapKeycloakError: (error: string): string => {
    const errorMap: Record<string, string> = {
      'invalid_grant': 'Nieprawidłowe dane logowania',
      'unauthorized': 'Nieprawidłowa nazwa użytkownika lub hasło',
      'account_disabled': 'Konto zostało zablokowane',
      'account_temporarily_disabled': 'Konto zostało tymczasowo zablokowane',
      'invalid_user_credentials': 'Nieprawidłowe dane uwierzytelniające'
    };
    
    return errorMap[error] || 'Wystąpił błąd podczas logowania';
  }
};